import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { createLogger } from '@/shared/logging/logger'
import { ProjectForm } from '@/features/projects/components/project-form'
import { AuthUserMenu } from '@/features/auth/components/auth-user-menu'
import { authConfig } from '@/features/auth/config'
import { requireAuthBeforeLoad } from '@/features/auth/guards'
import { useCreateProject } from '@/features/projects/hooks/use-project-actions'
import { useProjectStore } from '@/features/projects/stores/project-store'
import { FreeCutLogo } from '@/components/brand/freecut-logo'
import { Button } from '@/components/ui/button'
import type { ProjectFormData } from '@/features/projects/utils/validation'

const logger = createLogger('NewProject')

type NewProjectSearch = {
  assistant?: boolean
}

export const Route = createFileRoute('/projects/new')({
  validateSearch: (search: Record<string, unknown>): NewProjectSearch => ({
    assistant: search.assistant === true || search.assistant === 'true',
  }),
  component: NewProject,
  beforeLoad: async ({ location }) => {
    requireAuthBeforeLoad({ location: location as any })
    try {
      const { loadProjects } = useProjectStore.getState()
      await loadProjects()
    } catch (err) {
      logger.warn('Failed to pre-load projects in beforeLoad:', err)
    }
  },
})

function NewProject() {
  const navigate = useNavigate()
  const { assistant } = Route.useSearch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submittingLabel, setSubmittingLabel] = useState('创建项目')
  const createProject = useCreateProject()

  const handleSubmit = async (data: ProjectFormData) => {
    setIsSubmitting(true)
    setSubmittingLabel('正在创建项目...')

    try {
      const result = await createProject(data)

      if (result.success && result.project) {
        setSubmittingLabel('正在进入编辑器...')
        await navigate({
          to: '/editor/$projectId',
          params: { projectId: result.project.id },
          search: assistant ? { assistant: true } : undefined,
        })
      } else {
        toast.error('创建项目失败', { description: result.error })
        setIsSubmitting(false)
        setSubmittingLabel('创建项目')
      }
    } catch (error) {
      logger.error('Failed to create project:', error)
      toast.error('创建项目失败', { description: '请稍后重试' })
      setIsSubmitting(false)
      setSubmittingLabel('创建项目')
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="panel-header border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/">
            <FreeCutLogo variant="full" size="md" className="hover:opacity-80 transition-opacity" />
          </Link>
          <div className="flex items-center gap-3">
            {authConfig.mainSiteUrl ? (
              <Button variant="outline" asChild>
                <a href={authConfig.mainSiteUrl}>返回主站</a>
              </Button>
            ) : null}
            <AuthUserMenu />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <ProjectForm
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          submittingLabel={submittingLabel}
          hideHeader={true}
        />
      </div>
    </div>
  )
}
